import { useEffect } from 'react';
import { getPost, posts } from './blog/posts';
import { useLanguage } from './i18n/useLanguage';

const UI = {
  en: { notFound: 'Article not found.', back: 'Back to blog', min: 'min read', home: 'Home', cta: 'Try White Bot free →', asideDesc: 'Automate Whiteout Survival on your own PC and control it from the web. 5-day free trial.' },
  es: { notFound: 'Artículo no encontrado.', back: 'Volver al blog', min: 'min de lectura', home: 'Inicio', cta: 'Prueba White Bot gratis →', asideDesc: 'Automatiza Whiteout Survival en tu propio PC y contrólalo desde la web. Prueba gratis 5 días.' },
  pt: { notFound: 'Artigo não encontrado.', back: 'Voltar ao blog', min: 'min de leitura', home: 'Início', cta: 'Teste White Bot grátis →', asideDesc: 'Automatize Whiteout Survival no seu próprio PC e controle pela web. Teste grátis de 5 dias.' },
  fr: { notFound: 'Article introuvable.', back: 'Retour au blog', min: 'min de lecture', home: 'Accueil', cta: 'Essayez White Bot →', asideDesc: 'Automatisez Whiteout Survival sur votre propre PC et contrôlez-le depuis le web. 5 jours d\'essai gratuit.' },
  de: { notFound: 'Artikel nicht gefunden.', back: 'Zurück zum Blog', min: 'Min. Lesezeit', home: 'Startseite', cta: 'White Bot kostenlos testen →', asideDesc: 'Automatisiere Whiteout Survival auf deinem eigenen PC und steuere es über das Web. 5 Tage kostenlos testen.' },
};

export function BlogPostPage({ slug }: { slug: string }) {
  const { lang, t } = useLanguage();
  const post = getPost(slug);
  // Idiomas de la oleada 2026-07 (ru/ko/zh/ja/tr): el blog es EN/ES, su UI cae a EN
  const ui = UI[lang as keyof typeof UI] ?? UI.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="lp blog-page">
        <nav className="lp-nav">
          <a href="/" className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></a>
          <div className="lp-nav-links">
            <a href="/blog">{ui.back}</a>
            <a href="/dashboard" className="lp-btn-outline">{t.nav.enter}</a>
          </div>
        </nav>
        <div className="blog-shell" style={{ textAlign: 'center', padding: '120px 24px' }}>
          <p>{ui.notFound}</p>
          <a href="/blog">{ui.back}</a>
        </div>
      </div>
    );
  }

  const content = lang === 'es' ? post.es : post.en;

  // Inject meta tags dynamically for SEO
  useEffect(() => {
    document.title = `${content.title} | White Bot`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', content.description);
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.setAttribute('content', content.keywords);
    return () => {
      document.title = 'White Bot — El bot de Whiteout Survival en tu PC. Prueba gratis 5 días';
    };
  }, [content]);

  const schemaArticle = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: content.description,
    datePublished: post.publishedAt,
    author: { '@type': 'Organization', name: 'White Bot' },
    publisher: { '@type': 'Organization', name: 'White Bot', url: 'https://whitebot.app' },
    url: `https://whitebot.app/blog/${post.slug}`,
    inLanguage: lang === 'es' ? 'es' : 'en',
  });

  return (
    <div className="lp blog-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaArticle }} />
      <nav className="lp-nav">
        <a href="/" className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></a>
        <div className="lp-nav-links">
          <a href="/blog">{ui.back}</a>
          <a href="/dashboard" className="lp-btn-outline">{t.nav.enter}</a>
        </div>
      </nav>

      <div className="blog-shell">
        <article className="blog-article">
          <header className="blog-article-header">
            <div className="blog-article-meta">
              <a href="/blog" className="blog-back-link">← {ui.back}</a>
              <span>{post.publishedAt} · {post.readingMinutes} {ui.min}</span>
            </div>
            <h1>{content.title}</h1>
            <p className="blog-article-lead">{content.description}</p>
          </header>

          <div
            className="blog-article-body"
            dangerouslySetInnerHTML={{ __html: content.body }}
          />

          <div className="blog-article-cta">
            <a href="/" className="lp-btn-primary">{ui.cta}</a>
          </div>
        </article>

        <aside className="blog-aside">
          <div className="blog-aside-card">
            <strong>White Bot</strong>
            <p>{ui.asideDesc}</p>
            <a href="/" className="lp-btn-primary" style={{ fontSize: '14px', padding: '10px 18px' }}>{ui.cta}</a>
          </div>
          <div className="blog-aside-card">
            <strong>More guides</strong>
            <ul className="blog-aside-list">
              {posts.filter((p) => p.slug !== slug).map((p) => (
                <li key={p.slug}>
                  <a href={`/blog/${p.slug}`}>{lang === 'es' ? p.es.title : p.en.title}</a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <span className="lp-footer-logo">White Bot</span>
          <nav className="lp-footer-links">
            <a href="/">{ui.home}</a>
            <a href="/blog">Blog</a>
            <a href="/dashboard">{t.nav.enter}</a>
          </nav>
          <p className="lp-footer-copy">{t.footer.copy}</p>
        </div>
      </footer>
    </div>
  );
}
